import {
  type FieldType,
  INDEX_CAP_PER_PROJECT,
  INDEXABLE_TYPES,
  LONG_TEXT_MAX_LENGTH,
  TEXT_MAX_LENGTH,
} from '@servicetim/shared';
import { Checkbox, Input } from '@servicetim/ui/components';
import { type ReactNode, useId } from 'react';
import { Controller, type FieldErrors, useFormContext } from 'react-hook-form';
import { CheckboxList, type CheckboxListOption } from '../common/checkbox-list';
import { FormField, fieldA11y } from '../common/form-field';

/** What the config sub-form reads from the enclosing `react-hook-form` context. */
interface ConfigShape {
  config: Record<string, unknown>;
}

interface FlagsShape {
  indexed?: boolean;
  enforceUnique?: boolean;
}

export interface FieldConfigFormProps {
  type: FieldType;
  /** Tenant-wide role templates for `restrictToRoleIds` (user / multi_user). */
  roleOptions?: CheckboxListOption[];
  /** Projects for `allowedProjectIds` (ticket_lookup). */
  projectOptions?: CheckboxListOption[];
  disabled?: boolean;
}

const toNullableNumber = (value: unknown) =>
  value === '' || value === null || value === undefined ? null : Number(value);
const toNullableString = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? null : value;

function configError(errors: FieldErrors<ConfigShape>, key: string): string | undefined {
  const config = errors.config as unknown as
    | Record<string, { message?: string } | undefined>
    | undefined;
  return config?.[key]?.message;
}

/**
 * Per-type settings of a field definition, driven by the `FieldConfigSchema` variants. Registers
 * `config.<key>` on the parent form; switch `type` and the parent resets `config` to the defaults
 * of the new type (`defaultConfigFor`).
 */
export function FieldConfigForm({
  type,
  roleOptions = [],
  projectOptions = [],
  disabled,
}: FieldConfigFormProps) {
  const id = useId();
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<ConfigShape>();

  const numberField = (
    key: string,
    label: string,
    options: {
      min?: number;
      max?: number;
      step?: number;
      nullable?: boolean;
      description?: ReactNode;
    } = {},
  ) => {
    const error = configError(errors, key);
    return (
      <FormField
        key={key}
        label={label}
        htmlFor={`${id}-${key}`}
        description={options.description}
        error={error}
      >
        <Input
          type="number"
          inputMode="decimal"
          min={options.min}
          max={options.max}
          step={options.step ?? 1}
          disabled={disabled}
          {...register(
            `config.${key}`,
            options.nullable ? { setValueAs: toNullableNumber } : { valueAsNumber: true },
          )}
          {...fieldA11y(`${id}-${key}`, { description: options.description, error })}
        />
      </FormField>
    );
  };

  const textField = (
    key: string,
    label: string,
    options: { maxLength?: number; description?: ReactNode; placeholder?: string } = {},
  ) => {
    const error = configError(errors, key);
    return (
      <FormField
        key={key}
        label={label}
        htmlFor={`${id}-${key}`}
        description={options.description}
        error={error}
      >
        <Input
          maxLength={options.maxLength}
          placeholder={options.placeholder}
          disabled={disabled}
          {...register(`config.${key}`, { setValueAs: toNullableString })}
          {...fieldA11y(`${id}-${key}`, { description: options.description, error })}
        />
      </FormField>
    );
  };

  const idList = (
    key: string,
    label: string,
    options: CheckboxListOption[],
    description: ReactNode,
    emptyHint: string,
  ) => {
    const error = configError(errors, key);
    return (
      <div key={key} className="space-y-1.5">
        <p id={`${id}-${key}-label`} className="font-medium text-fg text-sm leading-none">
          {label}
        </p>
        <p id={`${id}-${key}-description`} className="text-fg-muted text-xs">
          {description}
        </p>
        {options.length === 0 ? (
          <p className="text-fg-muted text-sm">{emptyHint}</p>
        ) : (
          <Controller
            control={control}
            name={`config.${key}`}
            render={({ field }) => (
              <CheckboxList
                id={`${id}-${key}`}
                aria-labelledby={`${id}-${key}-label`}
                aria-describedby={`${id}-${key}-description`}
                aria-invalid={Boolean(error)}
                options={options}
                columns={2}
                disabled={disabled}
                value={Array.isArray(field.value) ? (field.value as string[]) : []}
                onChange={(next) => field.onChange(next.length === 0 ? null : next)}
              />
            )}
          />
        )}
        {error ? (
          <p role="alert" className="text-red-600 dark:text-red-400 text-xs">
            {error}
          </p>
        ) : null}
      </div>
    );
  };

  let fields: ReactNode[];
  switch (type) {
    case 'text':
      fields = [
        numberField('maxLength', 'Max length', { min: 1, max: TEXT_MAX_LENGTH }),
        textField('pattern', 'Pattern', {
          maxLength: 200,
          placeholder: '^[A-Z]{3}-\\d+$',
          description: 'Optional regular expression values must match.',
        }),
      ];
      break;
    case 'long_text':
      fields = [numberField('maxLength', 'Max length', { min: 1, max: LONG_TEXT_MAX_LENGTH })];
      break;
    case 'number':
      fields = [
        numberField('min', 'Minimum', { nullable: true, step: 0.000001 }),
        numberField('max', 'Maximum', { nullable: true, step: 0.000001 }),
        numberField('precision', 'Precision', {
          min: 0,
          max: 6,
          description: 'Decimal places allowed (0–6).',
        }),
        textField('unit', 'Unit', { maxLength: 16, placeholder: 'kg, €, hours…' }),
      ];
      break;
    case 'multi_select':
      fields = [numberField('maxSelections', 'Max selections', { min: 1, max: 50 })];
      break;
    case 'user':
      fields = [
        idList(
          'restrictToRoleIds',
          'Restrict to roles',
          roleOptions,
          'Only members of the chosen tenant-wide role templates can be picked. Leave empty for anyone.',
          'No role templates yet; anyone can be picked.',
        ),
      ];
      break;
    case 'multi_user':
      fields = [
        numberField('maxSelections', 'Max selections', { min: 1, max: 50 }),
        idList(
          'restrictToRoleIds',
          'Restrict to roles',
          roleOptions,
          'Only members of the chosen tenant-wide role templates can be picked. Leave empty for anyone.',
          'No role templates yet; anyone can be picked.',
        ),
      ];
      break;
    case 'tags':
      fields = [numberField('maxTags', 'Max tags', { min: 1, max: 50 })];
      break;
    case 'ticket_lookup':
      fields = [
        idList(
          'allowedProjectIds',
          'Allowed projects',
          projectOptions,
          'Tickets can only be picked from these projects. Leave empty for every visible project.',
          'No projects yet; every visible project is allowed.',
        ),
      ];
      break;
    default:
      fields = [];
  }

  if (fields.length === 0) {
    return (
      <p
        className="rounded-md bg-surface-sunken px-3 py-2 text-fg-muted text-sm"
        data-slot="field-config-empty"
      >
        No additional settings for this type.
      </p>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-slot="field-config" data-field-type={type}>
      {fields}
    </div>
  );
}

/** `indexed` / `enforceUnique` flags with the registry rules explained inline. */
export function FieldFlags({ type, disabled }: { type: FieldType; disabled?: boolean }) {
  const id = useId();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FlagsShape>();
  const indexable = INDEXABLE_TYPES.has(type);
  const indexed = watch('indexed') === true;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <FormField
        inline
        label="Indexed"
        htmlFor={`${id}-indexed`}
        description={
          indexable
            ? `Sortable and range-filterable; at most ${INDEX_CAP_PER_PROJECT} indexed fields per project.`
            : `${type} fields cannot be indexed.`
        }
        error={errors.indexed?.message}
      >
        <Controller
          control={control}
          name="indexed"
          render={({ field }) => (
            <Checkbox
              id={`${id}-indexed`}
              checked={field.value === true}
              disabled={disabled || !indexable}
              onCheckedChange={(checked) => {
                field.onChange(checked === true);
                if (checked !== true) setValue('enforceUnique', false, { shouldDirty: true });
              }}
              aria-describedby={`${id}-indexed-description`}
            />
          )}
        />
      </FormField>
      <FormField
        inline
        label="Enforce unique values"
        htmlFor={`${id}-unique`}
        description="Requires indexed; two tickets of a project cannot share a value."
        error={errors.enforceUnique?.message}
      >
        <Controller
          control={control}
          name="enforceUnique"
          render={({ field }) => (
            <Checkbox
              id={`${id}-unique`}
              checked={field.value === true}
              disabled={disabled || !indexed}
              onCheckedChange={(checked) => field.onChange(checked === true)}
              aria-describedby={`${id}-unique-description`}
            />
          )}
        />
      </FormField>
    </div>
  );
}
